import React, { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Boxes, AlertTriangle, Info } from 'lucide-react';
import { labImages } from '../services/api';
import {
  normalizeCatalog,
  findDefaultEntry,
  selectableEntries,
  orderEntries,
  memoryShortfall,
  parseMemoryToMiB,
  formatMemory,
  shortImageRef,
  FALLBACK_DEFAULT_ENTRY,
} from '../utils/imageCatalog';

/**
 * Lab environment picker — shared by the lab settings page and the create-lab
 * dialog so both surfaces speak about the runtime in exactly the same terms.
 *
 * The visual language is borrowed wholesale from the "Memory per student"
 * control it sits above: two-line option buttons, aria-pressed, --badge-info-bg
 * when active, 0.55 opacity when the form is read-only, a conditional
 * AlertTriangle, and a closing caveat about propagation. The two controls are
 * one decision ("what does this student's machine look like"), so they must not
 * look like two different products.
 */

/**
 * Load the catalogue.
 *
 * The catalogue endpoint lives in a backend that deploys on its own schedule,
 * so this bundle can genuinely be serving faculty before those routes exist. A
 * failure here must never take the lab settings page with it: the hook reports
 * `available: false`, callers fall back to showing the platform default as a
 * fixed, read-only fact, and nothing submits an image. The worst case is "you
 * cannot change the environment yet", not a broken page.
 */
export function useImageCatalog({ includeRetired = false, enabled = true } = {}) {
  const query = useQuery({
    queryKey: ['lab-images', includeRetired],
    queryFn: () => labImages.list(includeRetired ? { includeRetired: true } : {}),
    enabled,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

  const entries = useMemo(
    () => (query.isError ? [] : normalizeCatalog(query.data)),
    [query.data, query.isError]
  );

  const status = query.error?.response?.status;
  const unavailableReason = !query.isError
    ? null
    : (status === 404
      ? 'This version of the platform backend does not offer an environment catalogue yet.'
      : (query.error?.response?.data?.message
        || query.error?.response?.data?.error
        || 'The environment catalogue could not be loaded.'));

  return {
    entries,
    defaultEntry: entries.length ? findDefaultEntry(entries) : FALLBACK_DEFAULT_ENTRY,
    // Returned to admins only. The server refuses an image outside these
    // registries, so knowing them turns the add form's guess into the real rule.
    allowedRegistries: Array.isArray(query.data?.allowedRegistries)
      ? query.data.allowedRegistries
      : null,
    available: entries.length > 0,
    isLoading: query.isLoading,
    isError: query.isError,
    unavailableReason,
    refetch: query.refetch,
  };
}

/** Second line of an option button: what the environment is, then what it costs. */
function optionHint(opt) {
  if (opt.unknown) return `Retired or unrecognised — ${shortImageRef(opt.image)}`;
  const base = opt.description
    || (opt.toolchains.length ? opt.toolchains.join(', ') : '')
    || shortImageRef(opt.image);
  const prefix = opt.retired ? 'Retired — ' : '';
  const memory = opt.suggestedMemory ? ` · needs ${formatMemory(opt.suggestedMemory)}` : '';
  return `${prefix}${base}${memory}`;
}

/**
 * The option buttons. `selected` is a whole entry rather than a string, so that
 * a lab storing the legacy unqualified `lab-environment:latest` and a catalogue
 * entry for the same environment compare equal and do not read as a pending
 * change.
 */
export function ImageOptionGroup({
  entries,
  selected,
  onSelect,
  disabled = false,
  ariaLabel = 'Lab environment',
  testIdPrefix = 'lab-image',
}) {
  const options = useMemo(() => {
    const base = orderEntries(selectableEntries(entries));
    // A lab pointing at a retired or off-catalogue image still has to see its
    // own value. Without this the picker would show the default as selected and
    // the next save would quietly move the whole class to a new runtime.
    if (selected && !base.some((e) => e.key === selected.key)) {
      return [selected, ...base];
    }
    return base;
  }, [entries, selected]);

  return (
    <div
      role="group"
      aria-label={ariaLabel}
      data-testid={`${testIdPrefix}-picker`}
      style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}
    >
      {options.map((opt) => {
        const active = !!(selected && selected.key === opt.key);
        // A retired entry is shown (so the lab on it can see it) but never chosen.
        const optDisabled = disabled || opt.retired || opt.synthetic;
        const ref = opt.resolvedImage || opt.image;

        return (
          <button
            key={opt.key}
            type="button"
            aria-pressed={active}
            disabled={optDisabled}
            data-testid={`${testIdPrefix}-option-${opt.key}`}
            title={ref ? `${opt.name} — ${ref}` : opt.name}
            onClick={() => { if (!optDisabled && onSelect) onSelect(opt); }}
            style={{
              display: 'flex', flexDirection: 'column', gap: 2, textAlign: 'left',
              flex: '1 1 200px', maxWidth: 320,
              padding: '9px 14px', borderRadius: 8, fontFamily: 'inherit',
              cursor: optDisabled ? 'default' : 'pointer',
              background: active ? 'var(--badge-info-bg)' : 'var(--bg-tertiary)',
              color: active ? 'var(--accent-primary)' : 'var(--text-secondary)',
              border: `1px solid ${active ? 'var(--accent-primary)' : 'var(--border-primary)'}`,
              opacity: optDisabled && !active ? 0.55 : 1,
            }}
          >
            <span style={{ fontWeight: 600, fontSize: 13.5 }}>
              {opt.name}
              {opt.isDefault && !opt.unknown && (
                <span style={{ fontWeight: 500, opacity: 0.8 }}> · Default</span>
              )}
            </span>
            <span style={{ fontSize: 11, opacity: 0.8 }}>{optionHint(opt)}</span>
          </button>
        );
      })}
    </div>
  );
}

/**
 * "This environment wants a higher ceiling than the lab allows."
 *
 * Worth a hard warning rather than a hint: exceeding the memory limit does not
 * slow a student down, it ends their session, and it does so at the exact
 * moment they run the toolchain the environment was chosen for.
 */
export function ImageMemoryWarning({ entry, memoryLimit, suffix }) {
  const short = memoryShortfall(entry, memoryLimit);
  if (!short) return null;
  return (
    <p role="status" data-testid="lab-image-memory-warning" style={{
      display: 'flex', alignItems: 'flex-start', gap: 7, marginTop: 12, marginBottom: 0,
      fontSize: 12.5, color: 'var(--accent-warning)',
    }}>
      <AlertTriangle size={13} aria-hidden="true" style={{ marginTop: 2, flexShrink: 0 }} />
      <span>
        {entry.name} needs about {formatMemory(short.suggested)} and this lab allows{' '}
        {formatMemory(short.configured)}. Students will be cut off the first time they build
        or run. {suffix || `Raise "Memory per student" to ${formatMemory(short.suggested)} below.`}
      </span>
    </p>
  );
}

/** Explanatory block for when the catalogue could not be loaded. */
function CatalogUnavailableNote({ reason }) {
  return (
    <p role="status" data-testid="lab-image-unavailable" style={{
      display: 'flex', alignItems: 'flex-start', gap: 7, marginTop: 12, marginBottom: 0,
      fontSize: 12.5, color: 'var(--text-muted)',
    }}>
      <Info size={13} aria-hidden="true" style={{ marginTop: 2, flexShrink: 0 }} />
      <span>{reason} Every lab runs the standard environment until it does.</span>
    </p>
  );
}

/**
 * The lab-settings composition: a nested card holding the picker, the memory
 * cross-check, the change warning, and the propagation caveat.
 *
 * `selected` / `original` are entries; `onSelect` receives an entry.
 */
export function LabImageCard({
  entries,
  available,
  isLoading,
  unavailableReason,
  selected,
  original,
  onSelect,
  isEditing,
  memoryLimit,
  adoptsSuggestedMemory = false,
}) {
  const changed = Boolean(selected && original && selected.key !== original.key);
  const shownSelected = available ? selected : FALLBACK_DEFAULT_ENTRY;
  const shownEntries = available ? entries : [FALLBACK_DEFAULT_ENTRY];

  // The operator pinned a ceiling below what this environment reserves. The server
  // validates the two together and refuses the save, so say so before they press Save
  // rather than showing them the rejection afterwards.
  const pinnedBelowRequest = Boolean(
    changed && !adoptsSuggestedMemory && selected?.suggestedMemoryRequest
    && parseMemoryToMiB(selected.suggestedMemoryRequest) !== null
    && parseMemoryToMiB(memoryLimit) !== null
    && parseMemoryToMiB(selected.suggestedMemoryRequest) > parseMemoryToMiB(memoryLimit)
  );

  return (
    <div className="card" style={{ padding: 20, marginTop: 16 }} data-testid="lab-image-card">
      <h4 style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, fontSize: 15 }}>
        <Boxes size={16} aria-hidden="true" /> Lab environment
      </h4>
      <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '0 0 14px' }}>
        The toolchain every student in this lab gets. Starter code means nothing without it —
        a Flutter project in a Python environment will not build.
      </p>

      {isLoading ? (
        <div role="status" aria-label="Loading environments" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0' }}>
          <span className="spinner" style={{ width: 16, height: 16 }} aria-hidden="true" />
          <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>Loading environments…</span>
        </div>
      ) : (
        <ImageOptionGroup
          entries={shownEntries}
          selected={shownSelected}
          onSelect={onSelect}
          disabled={!isEditing || !available}
          ariaLabel="Lab environment"
        />
      )}

      {!isLoading && !available && <CatalogUnavailableNote reason={unavailableReason} />}

      {available && adoptsSuggestedMemory && (
        <p role="status" data-testid="lab-image-memory-adopt" style={{
          display: 'flex', alignItems: 'flex-start', gap: 7, marginTop: 12, marginBottom: 0,
          fontSize: 12.5, color: 'var(--accent-primary)',
        }}>
          <Info size={13} aria-hidden="true" style={{ marginTop: 2, flexShrink: 0 }} />
          <span>
            Saving also sets the memory ceiling to {formatMemory(selected.suggestedMemory)}, which
            is what this environment needs. Pick a value under "Memory per student" to override it.
          </span>
        </p>
      )}

      {available && !adoptsSuggestedMemory && (
        <ImageMemoryWarning entry={selected} memoryLimit={memoryLimit} />
      )}

      {pinnedBelowRequest && (
        <p role="alert" data-testid="lab-image-memory-blocked" style={{
          display: 'flex', alignItems: 'flex-start', gap: 7, marginTop: 12, marginBottom: 0,
          fontSize: 12.5, color: 'var(--accent-danger)',
        }}>
          <AlertTriangle size={13} aria-hidden="true" style={{ marginTop: 2, flexShrink: 0 }} />
          <span>
            This will not save. {selected.name} reserves{' '}
            {formatMemory(selected.suggestedMemoryRequest)} per student, which is more than the
            ceiling of {formatMemory(memoryLimit)} set below — Kubernetes rejects a pod whose
            limit is under its request. Raise the ceiling to at least{' '}
            {formatMemory(selected.suggestedMemoryRequest)}.
          </span>
        </p>
      )}

      {changed && (
        <p role="status" data-testid="lab-image-change-warning" style={{
          display: 'flex', alignItems: 'flex-start', gap: 7, marginTop: 12, marginBottom: 0,
          fontSize: 12.5, color: 'var(--accent-warning)',
        }}>
          <AlertTriangle size={13} aria-hidden="true" style={{ marginTop: 2, flexShrink: 0 }} />
          <span>
            Changing this mid-semester splits the class. Students whose environment is already
            running stay on <strong>{original.name}</strong>, and <strong>Recreate All skips
            anything that is running</strong> — it reports success without touching them. Stop
            those environments first, then recreate, and expect students to lose whatever was
            open at the time.
          </span>
        </p>
      )}

      <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 12, marginBottom: 0 }}>
        Applies to environments created from now on. Existing student environments keep the one
        they were built with until they are deleted and recreated. Workspace files survive a
        recreate; the running session does not.
      </p>
    </div>
  );
}

export default LabImageCard;
